import PropTypes from "prop-types";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDebouncedValue } from "hooks/useDebouncedValue";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { FiEdit2, FiFilter, FiMoreVertical, FiSearch, FiXCircle } from "react-icons/fi";
import Badge from "components/Badge";
import Card from "components/Card";
import AppBreadcrumbs from "components/common/AppBreadcrumbs";
import AppDataTable from "components/common/AppDataTable";
import AppPagination from "components/common/AppPagination";
import ListFetchErrorBanner from "components/common/ListFetchErrorBanner";
import ActionMenuPopover from "components/common/ActionMenuPopover";
import FormDatePicker from "components/form/FormDatePicker";
import { apiURl } from "store/actions";
import { buildQueryString } from "utils/helper";
import { api, isSuccessResponse } from "lib/api";
import { API_ENDPOINTS } from "lib/apiUrl";
import swtAlert from "lib/sweetAlert";
import ProjectChangeStatusModal from "pages/project/ProjectChangeStatusModal";
import {
  mapProjectListRow,
  getProjectIsSampleColumn,
  getProjectShareColumn,
  parseProjectsListResponse,
  PROJECT_STATUS_OPTIONS,
  normalizeProjectStatus,
  projectStatusLabel,
} from "utils/projectsList";

const DEFAULT_LIMIT = 10;
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const FILTER_INITIAL_VALUES = {
  page: 1,
  limit: DEFAULT_LIMIT,
  search: "",
  status: "",
  date: "",
};

function ProjectTitleCell(row) {
  return <span className="font-semibold text-text-900">{row.title}</span>;
}

function stripHtml(html) {
  return String(html || "")
    .replaceAll(/<[^>]*>/g, " ")
    .replaceAll(/\s+/g, " ")
    .trim();
}

function ProjectDescriptionCell(row) {
  return (
    <p className="line-clamp-2 max-w-[280px] text-sm text-text-700">
      {stripHtml(row.description)}
    </p>
  );
}

function FundsCell(row) {
  return <span className="font-semibold text-text-900">{row.fundsRaised}</span>;
}

function HardCapCell(row) {
  return <span className="text-text-800">{row.hardCap}</span>;
}

function SharePriceCell(row) {
  return <span className="text-text-800">{row.sharePrice}</span>;
}

function TotalFundsRequiredCell(row) {
  return <span className="text-text-800">{row.totalFundsRequired}</span>;
}

function DaysLeftCell(row) {
  return <span className="text-text-800">{row.campaignDaysLeft}</span>;
}

function InvestorsCell(row) {
  return <span className="text-text-800">{row.investorCount}</span>;
}

function ProjectStatusCell({ row, onEditStatus }) {
  return (
    <div
      className="inline-flex items-center gap-1.5"
      onMouseDown={(event) => event.stopPropagation()}
      onClick={(event) => event.stopPropagation()}
    >
      <Badge status={row.status} />
      <button
        type="button"
        title="Change status"
        aria-label={`Change status for ${row.title}`}
        onClick={() => onEditStatus(row)}
        className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-slate-200 text-text-600 transition hover:border-brand-400 hover:bg-slate-50 hover:text-brand-900"
      >
        <FiEdit2 className="h-3.5 w-3.5" aria-hidden />
      </button>
    </div>
  );
}

ProjectStatusCell.propTypes = {
  row: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    title: PropTypes.string,
    status: PropTypes.string,
  }).isRequired,
  onEditStatus: PropTypes.func.isRequired,
};

const projectActionRowPropType = PropTypes.shape({
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
});

function ProjectRowActionMenu({ row, isOpen, onToggle, onClose }) {
  const anchorRef = useRef(null);
  const rowId = String(row.id);
  return (
    <>
      <button
        ref={anchorRef}
        type="button"
        data-project-action-menu
        className="relative inline-flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 text-text-700 transition hover:bg-slate-50"
        onMouseDown={(event) => event.stopPropagation()}
        onClick={(event) => {
          event.stopPropagation();
          onToggle();
        }}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-label="Open project actions"
      >
        <FiMoreVertical className="h-4 w-4" />
      </button>
      <ActionMenuPopover anchorRef={anchorRef} isOpen={isOpen} onClose={onClose} className="min-w-[8rem] w-32">
        <Link
          to={`/projects/${rowId}`}
          className="block px-3 py-2 text-xs font-semibold text-text-700 transition hover:bg-slate-50"
          onClick={(event) => {
            event.stopPropagation();
            onClose();
          }}
          role="menuitem"
        >
          View
        </Link>
        <Link
          to={`/projects/${rowId}/edit`}
          className="block border-t border-slate-100 px-3 py-2 text-xs font-semibold text-text-700 transition hover:bg-slate-50"
          onClick={(event) => {
            event.stopPropagation();
            onClose();
          }}
          role="menuitem"
        >
          Edit
        </Link>
      </ActionMenuPopover>
    </>
  );
}

ProjectRowActionMenu.propTypes = {
  row: projectActionRowPropType.isRequired,
  isOpen: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

function ProjectActionColumnCell({ row, openMenuId, onToggleId, onCloseMenu }) {
  return (
    <ProjectRowActionMenu
      row={row}
      isOpen={openMenuId === row.id}
      onToggle={() => onToggleId(row.id)}
      onClose={onCloseMenu}
    />
  );
}

ProjectActionColumnCell.propTypes = {
  row: projectActionRowPropType.isRequired,
  openMenuId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onToggleId: PropTypes.func.isRequired,
  onCloseMenu: PropTypes.func.isRequired,
};

function buildProjectsListColumns(
  openMenuId,
  toggleFn,
  closeFn,
  onEditStatus,
  isSampleHandlers,
) {
  return [
    {
      id: "title",
      header: "Title",
      selector: (row) => row.title,
      cell: ProjectTitleCell,
      sortable: true,
    },
    {
      id: "description",
      header: "Description",
      selector: (row) => row.descriptionPlain,
      cell: ProjectDescriptionCell,
      grow: 2,
      sortable: true,
    },
    {
      id: "fundsRaised",
      header: "Funds Raised",
      selector: (row) => row.fundsRaisedAmount,
      cell: FundsCell,
      sortable: true,
    },
    {
      id: "hardCap",
      header: "Hard Cap",
      selector: (row) => row.hardCapAmount,
      cell: HardCapCell,
      sortable: true,
    },
    {
      id: "sharePrice",
      header: "Share Price",
      selector: (row) => row.sharePriceAmount,
      cell: SharePriceCell,
      sortable: true,
    },
    {
      id: "totalFundsRequired",
      header: "Total Funds Required",
      selector: (row) => row.totalFundsRequiredAmount,
      cell: TotalFundsRequiredCell,
      sortable: true,
    },
    {
      id: "daysLeft",
      header: "Days Left",
      selector: (row) => row.campaignDaysLeftAmount,
      cell: DaysLeftCell,
      sortable: true,
    },
    {
      id: "investorCount",
      header: "Investors",
      selector: (row) => row.investorCountAmount,
      cell: InvestorsCell,
      sortable: true,
    },
    getProjectShareColumn(),
    getProjectIsSampleColumn(isSampleHandlers),
    {
      id: "status",
      header: "Status",
      selector: (row) => row.status,
      cell(row) {
        return <ProjectStatusCell row={row} onEditStatus={onEditStatus} />;
      },
      sortable: true,
    },
    {
      id: "action",
      header: "Action",
      cell(row) {
        return (
          <ProjectActionColumnCell
            row={row}
            openMenuId={openMenuId}
            onToggleId={toggleFn}
            onCloseMenu={closeFn}
          />
        );
      },
    },
  ];
}

export default function ProjectsListPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [showCreatedBanner, setShowCreatedBanner] = useState(false);
  const [data, setData] = useState({});
  const [filters, setFilters] = useState(FILTER_INITIAL_VALUES);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState("");
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [searchDraft, setSearchDraft] = useState(filters.search);
  const { debounced: debouncedSearch, flush: flushSearch } = useDebouncedValue(searchDraft, 400);
  const [openActionMenuId, setOpenActionMenuId] = useState(null);
  const [statusModalProject, setStatusModalProject] = useState(null);
  const [statusDraft, setStatusDraft] = useState("active");
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [isSampleUpdatingId, setIsSampleUpdatingId] = useState(null);

  const closeProjectActionMenu = useCallback(() => setOpenActionMenuId(null), []);

  const openStatusModal = useCallback((row) => {
    const current = normalizeProjectStatus(row.status, "draft");
    const defaultNextStatus =
      PROJECT_STATUS_OPTIONS.find((option) => option.value !== current)?.value ?? "draft";
    setStatusModalProject({ id: row.id, title: row.title, status: row.status });
    setStatusDraft(defaultNextStatus);
  }, []);

  const closeStatusModal = useCallback(() => {
    if (statusUpdating) return;
    setStatusModalProject(null);
  }, [statusUpdating]);

  const handleStatusUpdate = useCallback(async () => {
    if (!statusModalProject) return;

    const projectId = String(statusModalProject.id);
    const nextStatus = normalizeProjectStatus(statusDraft, "draft");
    const currentStatus = normalizeProjectStatus(statusModalProject.status, "draft");

    if (nextStatus === currentStatus) {
      closeStatusModal();
      return;
    }

    setStatusUpdating(true);
    try {
      const resp = await api.patch(API_ENDPOINTS.adminProjectById(projectId), {
        status: nextStatus,
      });
      if (!isSuccessResponse(resp)) {
        const msg = resp?.message || "Could not update project status.";
        swtAlert.error("Update failed", msg);
        return;
      }

      setData((prev) => ({
        ...prev,
        data: (prev.data ?? []).map((row) =>
          String(row.id) === projectId ? { ...row, status: nextStatus } : row,
        ),
      }));

      swtAlert.success(
        "Status updated",
        `Project status changed to ${projectStatusLabel(nextStatus)}.`,
      );
      setStatusModalProject(null);
    } catch (error) {
      const msg = error?.message || "Could not update project status.";
      swtAlert.error("Update failed", msg);
    } finally {
      setStatusUpdating(false);
    }
  }, [statusModalProject, statusDraft, closeStatusModal]);

  const handleIsSampleToggle = useCallback(async (row, nextValue) => {
    const projectId = String(row.id);
    const currentValue = Boolean(row.isSample);

    if (nextValue === currentValue) return;

    setIsSampleUpdatingId(projectId);
    try {
      const resp = await api.patch(API_ENDPOINTS.adminProjectById(projectId), {
        isSample: nextValue,
      });
      if (!isSuccessResponse(resp)) {
        const msg = resp?.message || "Could not update sample project setting.";
        swtAlert.error("Update failed", msg);
        return;
      }

      setData((prev) => ({
        ...prev,
        data: (prev.data ?? []).map((item) =>
          String(item.id) === projectId ? { ...item, isSample: nextValue } : item,
        ),
      }));

      swtAlert.success(
        "Sample project updated",
        nextValue
          ? "Project marked as a sample project."
          : "Project is no longer marked as a sample project.",
      );
    } catch (error) {
      const msg = error?.message || "Could not update sample project setting.";
      swtAlert.error("Update failed", msg);
    } finally {
      setIsSampleUpdatingId(null);
    }
  }, []);

  const toggleProjectActionMenu = useCallback((id) => {
    setOpenActionMenuId((prev) => (prev === id ? null : id));
  }, []);

  useEffect(() => {
    setSearchDraft(filters.search);
  }, [filters.search]);

  useEffect(() => {
    setFilters((prev) => {
      const nextSearch = debouncedSearch.trim();
      if (prev.search === nextSearch) return prev;
      return { ...prev, search: nextSearch, page: 1 };
    });
  }, [debouncedSearch]);

  useEffect(() => {
    const closeMenusOnOutsideClick = (event) => {
      const clickedInsideActionMenu =
        event.target.closest("[data-project-action-menu]") ||
        event.target.closest("[data-action-menu-popover]");
      if (!clickedInsideActionMenu) {
        setOpenActionMenuId(null);
      }
    };

    document.addEventListener("mousedown", closeMenusOnOutsideClick);
    document.addEventListener("touchstart", closeMenusOnOutsideClick);

    return () => {
      document.removeEventListener("mousedown", closeMenusOnOutsideClick);
      document.removeEventListener("touchstart", closeMenusOnOutsideClick);
    };
  }, []);

  useEffect(() => {
    if (location.state?.projectCreated) {
      setShowCreatedBanner(true);
      navigate(".", { replace: true, state: {} });
      const t = window.setTimeout(() => setShowCreatedBanner(false), 6000);
      return () => window.clearTimeout(t);
    }
    return undefined;
  }, [location.state, navigate]);

  const rows = useMemo(() => data?.data ?? [], [data]);
  const totalItems = useMemo(() => data?.total ?? 0, [data]);
  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(totalItems / (data?.limit || filters.limit))),
    [totalItems, filters.limit, data]
  );

  useEffect(() => {
    setListError("");
  }, [filters]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const endUrl = `${apiURl.projects}`;
      const qs = buildQueryString(filters);
      const path = qs ? `${endUrl}?${qs}` : endUrl;
      const resp = await api.get(path);
      if (!isSuccessResponse(resp)) {
        const msg = resp?.message || "Could not load projects.";
        setListError(msg);
        swtAlert.error("Projects", msg);
        return;
      }
      const { items, total } = parseProjectsListResponse(resp);
      setData({
        data: items.map(mapProjectListRow),
        total,
        limit: filters.limit,
      });
      setListError("");
    } catch (error) {
      const msg = error?.message || "Could not load projects.";
      setListError(msg);
      swtAlert.error("Projects", msg);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const isSampleHandlers = useMemo(
    () => ({
      onToggle: handleIsSampleToggle,
      updatingId: isSampleUpdatingId,
    }),
    [handleIsSampleToggle, isSampleUpdatingId],
  );

  const columns = useMemo(
    () =>
      buildProjectsListColumns(
        openActionMenuId,
        toggleProjectActionMenu,
        closeProjectActionMenu,
        openStatusModal,
        isSampleHandlers,
      ),
    [
      openActionMenuId,
      closeProjectActionMenu,
      toggleProjectActionMenu,
      openStatusModal,
      isSampleHandlers,
    ],
  );

  const activeFilterCount = [filters.status, filters.date].filter(Boolean).length;

  const projectsManagementCardAction = (
    <Link
      to="/projects/new"
      className="rounded-lg bg-brand-900 px-4 py-2 text-xs font-semibold text-white transition hover:bg-brand-800"
    >
      Add Project
    </Link>
  );

  const projectListFilterDrawer = filterDrawerOpen ? (
    <div className="mb-4 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
      <FormDatePicker
        label="Created date"
        name="project_list_date"
        value={filters.date}
        onChange={(event) => {
          setFilters({ ...filters, date: event.target.value, page: 1 });
        }}
        placeholder="Filter by date"
      />
      <div>
        <label htmlFor="filter-project-status" className="mb-1 block text-xs font-semibold text-text-500">
          Status
        </label>
        <select
          id="filter-project-status"
          value={filters.status === "" ? "all" : filters.status}
          onChange={(event) =>
            setFilters({
              ...filters,
              status: event.target.value === "all" ? "" : event.target.value,
              page: 1,
            })
          }
          className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm outline-none ring-brand-600 focus:ring-2"
        >
          <option value="all">All statuses</option>
          {PROJECT_STATUS_OPTIONS.map(({ value, label }) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
      </div>
    </div>
  ) : null;

  return (
    <>
      <ProjectChangeStatusModal
        open={Boolean(statusModalProject)}
        project={statusModalProject}
        statusDraft={statusDraft}
        updating={statusUpdating}
        onStatusChange={setStatusDraft}
        onClose={closeStatusModal}
        onUpdate={handleStatusUpdate}
      />

      <AppBreadcrumbs items={[{ label: "Projects", path: "/projects" }]} />
      {showCreatedBanner ? (
        <output
          className="mb-4 block rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm font-medium text-green-900"
          aria-live="polite"
        >
          Project submitted successfully. It will appear in the list after admin review.
        </output>
      ) : null}
      <Card title="Project Management" action={projectsManagementCardAction} className="mb-4" />
      <Card>
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="text-sm font-semibold text-text-900">Projects List</h3>
            <p className="text-xs text-text-500">Total projects: {totalItems}</p>
          </div>

          <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
            <div className="relative min-w-[200px] flex-1 sm:max-w-sm">
              <FiSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-text-500" />
              <input
                value={searchDraft}
                onChange={(event) => setSearchDraft(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    flushSearch();
                    setFilters((prev) => ({
                      ...prev,
                      search: searchDraft.trim(),
                      page: 1,
                    }));
                  }
                }}
                placeholder="Search projects..."
                className="w-full rounded-lg border border-slate-200 bg-white py-2 pl-9 pr-3 text-sm outline-none ring-brand-600 focus:ring-2"
              />
            </div>

            <button
              type="button"
              aria-label="Open filters"
              aria-expanded={filterDrawerOpen}
              onClick={() => setFilterDrawerOpen((open) => !open)}
              className="relative rounded-lg border border-slate-200 p-2 text-text-700 transition hover:bg-slate-50"
            >
              <FiFilter />
              {activeFilterCount > 0 ? (
                <span className="absolute -right-1 -top-1 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-brand-900 px-0.5 text-[10px] font-bold text-white">
                  {activeFilterCount}
                </span>
              ) : null}
            </button>

            {(searchDraft || filters.status || filters.date) && (
              <button
                type="button"
                aria-label="Clear filters"
                onClick={() => {
                  setSearchDraft("");
                  setFilters(FILTER_INITIAL_VALUES);
                }}
                className="rounded-lg border border-slate-200 p-2 text-text-700 transition hover:bg-slate-50"
              >
                <FiXCircle />
              </button>
            )}
          </div>
        </div>

        {projectListFilterDrawer}

        {listError ? (
          <ListFetchErrorBanner message={listError} onRetry={fetchData} loading={loading} />
        ) : null}

        <AppDataTable
          columns={columns}
          data={rows}
          rowKey="id"
          minWidth={1500}
          loading={loading}
          onRowClicked={(row) => navigate(`/projects/${row.id}`)}
        />
        <AppPagination
          currentPage={filters.page}
          totalPages={totalPages}
          onPageChange={(page) => setFilters({ ...filters, page })}
          totalItems={totalItems}
          pageSize={filters.limit}
          onPageSizeChange={(limit) => setFilters({ ...filters, limit, page: 1 })}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
        />
      </Card>
    </>
  );
}
